import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {AuthService, ReplaceableComponentsService, RestService, RoutesService} from '@abp/ng.core';
import { filter, take } from 'rxjs/operators';
import { eThemeSharedRouteNames, ToasterService, UserMenu, UserMenuService } from '@abp/ng.theme.shared';
import { ConfigStateService } from '@abp/ng.core';
import { eThemeLeptonXComponents, eUserMenuItems } from '@volosoft/abp.ng.theme.lepton-x';
import { eSettingManagementRouteNames } from '@abp/ng.setting-management';
import { NavItemsComponent } from './nav-items/nav-items.component';
import { ThemeService, StyleNames, lightTheme } from '@volosoft/ngx-lepton-x';
import { ManageProfileTabsService } from '@volo/abp.ng.account/public/config';
import { NavigationEnd, Route, Router } from '@angular/router';
import { MobileNavbarComponent } from './mobile-navbar';
import { CustomTenantsComponent } from './tenants/tenants.component';
import { eSaasComponents } from '@volo/abp.ng.saas';
import { eAccountComponents } from '@volo/abp.ng.account/public';
import { RefreshPasswordComponent } from './refresh-password/components/refresh-password.component';
import { LoginComponent } from './login/login.component';
import { FcmService } from './services/fcm.service';
import { CustomToolbarComponent } from "./custom-nav-items/custom-toolbar.component";
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { NotificationStateService } from './services/notification-state.service';
import { NetworkStatusService } from './services/network-status.service';
import { EmptyLayoutComponent } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { AbpOAuthService } from '@abp/ng.oauth';
import { LogoutServiceService } from './logout/logout-service.service';
import { ConfigMenuService } from './config-menu/config-menu.service';
import { NavbarService, LpxNavbarItem } from '@volo/ngx-lepton-x.core';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-root',
  template: `
    <abp-loader-bar></abp-loader-bar>
    <abp-dynamic-layout></abp-dynamic-layout>
    <abp-gdpr-cookie-consent></abp-gdpr-cookie-consent>
    <abp-internet-status></abp-internet-status>
    <app-notification-panel></app-notification-panel>
    <app-offline-banner></app-offline-banner>
  `,
})

export class AppComponent implements OnInit {
  constructor(
    private replacableComponents: ReplaceableComponentsService,
    private themeService: ThemeService,
    private configMenu: ConfigMenuService,
    private authService: AuthService,
    private logoutService: LogoutServiceService,
    private userMenu: UserMenuService,
    private tabs: ManageProfileTabsService,
    private fcmService: FcmService,
    private config: ConfigStateService,
    private restService: RestService,
    private notificationStateService: NotificationStateService,
    private navbarService: NavbarService,
    private networkStatus: NetworkStatusService,
  ) {
    if (this.themeService.selectedStyle.styleName != StyleNames.Light) {
      this.themeService.setTheme(lightTheme);
    }
  }

  async getFcmToken() {
    return await this.fcmService.requestPermissionAndGetToken();
  }

  private router = inject(Router)
  private readonly navigateToFirstChildNavbarItem = (item?: LpxNavbarItem): boolean => {
    const link = this.findFirstNavbarLink(item?.children) || '/faturamento';
    this.router.navigateByUrl(link);

    return false;
  };

  openPrivacyPolicies() {
    // Converts the route into a string that can be used
    // with the window.open() function
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/privacy-policy`])
    );

    window.open(url, '_blank');
  }

  async ngOnInit(): Promise<void> {
    await this.syncNavbarActiveState();

    // Inicializa o FCM fora do APP_INITIALIZER: roda pós-render como
    // fire-and-forget, então uma falha aqui nunca trava o boot do app.
    this.fcmService.init().catch(err => console.error('Falha ao inicializar o FCM:', err));

    // Passa a acompanhar o estado de rede (banner offline e decisões de sessão).
    this.networkStatus.init().catch(err => console.error('Falha ao iniciar a deteccao de rede:', err));

    this.config.getOne$("currentUser").pipe(
      filter((u: any) => u?.isAuthenticated === true),
      take(1)
    ).subscribe(async (currentUser) => {
      this.notificationStateService.fetchInitialUnreadCount();

      const token = await this.fcmService.requestPermissionAndGetToken();

      if (token) {
        this.restService.request<any, any>(
          {
            method: 'POST',
            url: '/api/app/device-token',
            body: {
              tenantId: currentUser.tenantId,
              userId: currentUser.id,
              token: token
            }
          },
          { apiName: "Default" }
        ).subscribe({
          error: (err) => {
            console.error('Erro ao salvar o token:', err);
          }
        });
      }
    });


    // Atualiza o contador de notificações ao retornar ao app (background → foreground)
    if (Capacitor.isNativePlatform()) {
      App.addListener('resume', () => {
        // Offline, isso só erraria; o refresh resiliente da sessão no resume
        // virá junto do fix de refresh de token.
        if (this.networkStatus.isConnected) {
          this.notificationStateService.fetchInitialUnreadCount();
        }
      });
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe(async () => {
      await this.syncNavbarActiveState();
    });

    this.configMenu.configGlobal()
    this.configMenu.configTenants()

    //Remove general settings
    this.replacableComponents.add({
      component: NavItemsComponent,
      key: eThemeLeptonXComponents.Settings,
    });

    //Replace mobile navbar
    this.replacableComponents.add({
      component: MobileNavbarComponent,
      key: eThemeLeptonXComponents.NavbarMobile,
    });

    this.replacableComponents.add({
      component: CustomTenantsComponent,
      key: eSaasComponents.Tenants
    })

    this.replacableComponents.add({
      component: RefreshPasswordComponent,
      key: eAccountComponents.RefreshPassword
    })

    this.replacableComponents.add({
      component: LoginComponent,
      key: eAccountComponents.Login
    })
    this.replacableComponents.add({
      component: CustomToolbarComponent,
      key: eThemeLeptonXComponents.Toolbar,
    })
  }

  private async syncNavbarActiveState(): Promise<void> {
    const items = await firstValueFrom(this.navbarService.navbarItems$.pipe(take(1)));
    if (!items?.length) {
      return;
    }

    const currentUrl = this.getCurrentPath();
    let changed = false;

    const patchItems = (menuItems: LpxNavbarItem[]): LpxNavbarItem[] => {
      return menuItems.map(item => {
        const isFaturamentoMenu = item.text === 'Faturamento';
        const hasChildren = !!item.children?.length;
        const isParentMenu = hasChildren || isFaturamentoMenu;
        const children = hasChildren ? patchItems(item.children) : [];
        const childActive = children.some(child => child.expanded || child.selected);
        const selfActive = isParentMenu ? false : this.isActiveNavbarLink(item.link, currentUrl);
        const active = selfActive || childActive || (isFaturamentoMenu && this.isFaturamentoUrl(currentUrl));
        const expanded = active;
        const selected = active;
        const nextChildren = hasChildren ? children : item.children;
        const nextLink = isFaturamentoMenu ? '/faturamento' : (isParentMenu ? undefined : item.link);
        const nextAction = isFaturamentoMenu ? this.navigateToFirstChildNavbarItem : item.action;

        if (
          item.expanded === expanded &&
          item.selected === selected &&
          item.link === nextLink &&
          item.action === nextAction &&
          nextChildren === item.children
        ) {
          return item;
        }

        changed = true;
        return {
          ...item,
          link: nextLink,
          action: nextAction,
          expanded,
          selected,
          children: nextChildren,
        };
      });
    };

    const patched = patchItems(items);
    if (changed) {
      this.navbarService.setNavbarItems(...patched);
    }
  }

  private getCurrentPath(): string {
    return (this.router.url || '/').split('?')[0].split('#')[0] || '/';
  }

  private isActiveNavbarLink(link: string | undefined, currentUrl: string): boolean {
    if (!link) {
      return false;
    }

    const normalizedLink = link.split('?')[0].split('#')[0] || '/';
    if (normalizedLink === '/') {
      return currentUrl === '/';
    }

    return currentUrl === normalizedLink || currentUrl.startsWith(`${normalizedLink}/`);
  }

  private isFaturamentoUrl(currentUrl: string): boolean {
    return currentUrl === '/faturamento' || currentUrl.startsWith('/faturamento/');
  }

  private findFirstNavbarLink(items?: LpxNavbarItem[]): string | undefined {
    if (!items?.length) {
      return undefined;
    }

    for (const item of items) {
      if (item.link) {
        return item.link;
      }

      const childLink = this.findFirstNavbarLink(item.children);
      if (childLink) {
        return childLink;
      }
    }

    return undefined;
  }
}
